import {
  Button,
  Flex,
  FormField,
  Icon,
  TextInput,
  Typography,
} from '@percent/lemonade'
import { SubmitNominationFormPayload } from '@percent/product-nomination-form/api/nominationForm/nominationForm.types'
import {
  usePartnerConfig,
  useProgress,
} from '@percent/product-nomination-form/hooks'
import { Card } from '@percent/product-nomination-form/ui'
import { useEffect } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HostedFormStep } from '../../HostedForm.types'
import { useValidationStepData } from '../../validationStepContext/useValidationStepData/useValidationStepData'
import { ConfigurationList } from './ConfigurationList'

export const Nominees = () => {
  const { handleBack, handleNext } = useValidationStepData()
  const { partnerConfig } = usePartnerConfig()
  const { progress } = useProgress()
  const { t } = useTranslation()

  const handleNavigateNext = async () => {
    const test = await trigger('nominees')

    if (test) {
      return handleNext(HostedFormStep.TERMS)
    }
  }

  const {
    register,
    control,
    formState: { errors },
    trigger,
    getValues,
  } = useFormContext<SubmitNominationFormPayload>()

  const nominees = useFieldArray({ control, name: 'nominees' })

  const isSingleConfiguration =
    partnerConfig &&
    partnerConfig?.configuration.allowedConfigurations.length === 1

  const handleAddNominee = () => {
    nominees.append({
      firstName: '',
      lastName: '',
      email: '',
      validationSubmissionConfigurationIds: isSingleConfiguration
        ? [partnerConfig.configuration.allowedConfigurations[0].configurationId]
        : [],
      language: '',
    })
  }

  const handleRemoveNominee = (index: number) => {
    nominees.remove(index)
  }

  useEffect(() => {
    if (isSingleConfiguration) {
      nominees.replace(
        getValues('nominees').map((nominee) => ({
          ...nominee,
          validationSubmissionConfigurationIds: [
            partnerConfig.configuration.allowedConfigurations[0]
              .configurationId,
          ],
        }))
      )
    }
  }, [])

  return (
    <Card
      onBack={handleBack}
      onNext={handleNavigateNext}
      nextButtonDisabled={
        !!errors.nominees?.some?.(
          (n) => n && Object.values(n).filter(Boolean).length > 0
        )
      }
      nextButtonLabel={t('hostedValidation.button.next')}
      title={t('hostedValidation.title.nominees')}
      description={t('hostedValidation.title.nomineesSub')}
      progress={progress}
    >
      <Flex direction="column" gap={24} w="100%" align="start">
        {nominees.fields.map((nominee, index) => (
          <Flex direction="column" gap={8} w="100%" key={nominee.id}>
            <Flex
              direction="row"
              gap={10}
              justify="space-between"
              align="center"
            >
              <Typography variant="h4">
                {t('hostedValidation.typography.contactItem', {
                  index: index + 1,
                })}
              </Typography>
              {nominees.fields.length > 1 && (
                <Button
                  variant="reject"
                  size="small"
                  onClick={() => handleRemoveNominee(index)}
                >
                  <Flex align="center" justify="center">
                    <Icon name="reject" size={6} />
                  </Flex>
                </Button>
              )}
            </Flex>
            <Flex direction="row" gap={10}>
              <FormField
                label={t('hostedValidation.label.firstName')}
                statusMessage={errors.nominees?.[index]?.firstName?.message}
                status={
                  errors.nominees?.[index]?.firstName ? 'danger' : 'default'
                }
                necessity="required"
              >
                <TextInput
                  {...register(`nominees.${index}.firstName`)}
                  placeholder={t('hostedValidation.placeholder.firstName')}
                />
              </FormField>
              <FormField
                label={t('hostedValidation.label.lastName')}
                statusMessage={errors.nominees?.[index]?.lastName?.message}
                status={
                  errors.nominees?.[index]?.lastName ? 'danger' : 'default'
                }
                necessity="required"
              >
                <TextInput
                  {...register(`nominees.${index}.lastName`)}
                  placeholder={t('hostedValidation.placeholder.lastName')}
                />
              </FormField>
            </Flex>
            <FormField
              label={t('hostedValidation.label.email')}
              status={errors.nominees?.[index]?.email ? 'danger' : 'default'}
              statusMessage={errors.nominees?.[index]?.email?.message}
              necessity="required"
            >
              <TextInput
                {...register(`nominees.${index}.email`)}
                placeholder={t('hostedValidation.placeholder.email')}
              />
            </FormField>
            {!isSingleConfiguration && (
              <FormField
                label={t('hostedValidation.label.products')}
                status={
                  errors.nominees?.[index]?.validationSubmissionConfigurationIds
                    ? 'danger'
                    : 'default'
                }
                statusMessage={
                  errors.nominees?.[index]?.validationSubmissionConfigurationIds
                    ?.message
                }
                necessity="required"
              >
                <ConfigurationList nominee={nominee} nomineeIndex={index} />
              </FormField>
            )}
          </Flex>
        ))}
        <Button
          variant="primary"
          size="small"
          onClick={handleAddNominee}
          startIcon="add"
          startIconSize={4}
        >
          {t('hostedValidation.button.addContact')}
        </Button>
      </Flex>
    </Card>
  )
}
